import React, { useMemo } from 'react';
import {
  selectCashSection,
  selectCashRowForKey,
  selectLabCash,
  selectPaymentModeLine,
  selectCashTruncated,
  selectVisibleRows,
} from './monthlyReportModel';
import { STAFF_ROLES } from '../../constants/staff';
import { providerCategory } from '../../constants/visitProviderCategories';
import CashMemberTable from './CashMemberTable';
import './CashCollections.css';

// "OTC" = Outpatient Treatment Center (owner copy correction) — the SAME
// label the Manage Visits provider filter already uses for the nurse-led
// visit category, single-sourced from `visitProviderCategories.js` so this
// tab can never drift from the one ratified surface label.
const OTC_LABEL = providerCategory('NURSE').label;

/**
 * The exact user-facing strings (stories.md "Copy" table, §API CONTRACT FE
 * binding notes; ratified by business verdict.md, 2026-09-30 — P2 round 3).
 * Exported so a QA/copy test can diff it against the ratified table without
 * re-typing the strings — one source, both sides. `CashMemberTable.jsx`
 * (a sibling in the same feature) imports this object for its own two
 * ratified strings (`paymentModeLine`, `rowCountLine`) rather than keeping a
 * second copy — this file stays the ONE place every P2 cash-tab string lives.
 */
export const CASH_TAB_COPY = {
  basisLine: (monthLabel) => `Money collected in ${monthLabel}: each payment is counted in the month it was received. A refund or discount given this month on an earlier visit is taken off this month.`,
  doNotAddLine: 'These figures are money collected. IPD tabs show amounts billed at discharge. Do not add the two together.',
  opdHeading: 'OPD — money collected',
  otcHeading: `${OTC_LABEL} — money collected`,
  labHeading: 'Lab — money collected',
  radiologyHeading: 'Radiology — money collected',
  labTotalLabel: 'Total lab collected',
  radiologyTotalLabel: 'Total radiology collected',
  discountFootnote: 'Only returns marked "Discount" count as discounts; all other returns count as refunds.',
  opdCaption: 'Consultation money goes to the doctor the visit was first booked with, referral money to the doctor the patient was referred to, and procedure money to the doctor recorded on the procedure. A refund or discount goes to the same doctor as the payment it returns. Visits with no doctor are shown as "Unassigned".',
  negativeNote: "A doctor's total can be below zero when more was returned this month than was collected.",
  otcCaption: "Each OTC visit has one attending nurse or doctor; the money is shown against that person. A doctor's OTC money is shown here, not in the OPD table.",
  labByTechHeading: 'By lab technician',
  labSearchPlaceholder: 'Search lab technician…',
  labCaption: 'Lab money goes to the lab technician the test is assigned to. Tests with no technician are shown as "Unassigned". A refund or discount goes to the same technician as the payment it returns. Lab money for admitted patients is counted here, on money collected; it is not part of the IPD tabs.',
  // BUSY-VOUCHER R1 (OR-26/OR-47): imaging tests move from Lab to Radiology.
  // The sentence is appended to the Lab caption ONLY when the board carries a
  // `radiology` block (an older BE still keeps imaging inside Lab, so the
  // sentence would be false there).
  labImagingNote: 'Imaging tests (X-ray, ultrasound, CT, MRI, mammography) are shown under Radiology.',
  radiologyCaption: "Imaging tests: X-ray, ultrasound, CT, MRI and mammography, by each test's Type. Money goes to the technician the test is assigned to; a refund or discount goes to the same technician as the payment it returns. Radiology money for admitted patients is counted here, not in the IPD tabs.",
  labFilterNote: 'Lab is not filtered by doctor — organisation figures shown.',
  radiologyFilterNote: 'Radiology is not filtered by doctor — organisation figures shown.',
  opdFilterNotice: (doctor) => `Showing OPD and OTC figures for ${doctor} only. Choose "All doctors" to see the whole organisation.`,
  emptyOpd: (monthLabel) => `No OPD collections in ${monthLabel}.`,
  emptyOtc: (monthLabel) => `No OTC collections in ${monthLabel}.`,
  emptyLab: (monthLabel) => `No lab collections in ${monthLabel}.`,
  emptyRadiology: (monthLabel) => `No radiology collections in ${monthLabel}.`,
  filteredOpdEmpty: (doctor, monthLabel) => `No OPD collections for ${doctor} in ${monthLabel}.`,
  filteredOtcEmpty: (doctor, monthLabel) => `No OTC collections for ${doctor} in ${monthLabel}.`,
  footerOrg: 'Total — organisation',
  footerFor: (doctor) => `Total — ${doctor}`,
  truncatedBanner: (monthLabel) => `OPD, OTC, Lab and Radiology figures for ${monthLabel} are incomplete — not every payment could be read. The figures below may be understated.`,
  // M1 (business round 3): this line is ALWAYS organisation-level (the server
  // never computes a per-doctor mode split) — the prefix says so explicitly
  // even when the card above it is filtered to one doctor, so an accountant
  // reading "figures for Bala K only" right above it can't misread this
  // line as Bala K's own cash-by-mode split.
  paymentModeLine: (line) => `Whole organisation — by payment mode: ${line.map((m) => `${m.label} ${m.formatted}`).join(' · ')}`,
  rowCountLine: (n) => `Showing all ${n} row${n === 1 ? '' : 's'}${n > 8 ? ' — scroll the table to see the rest' : ''}.`,
};

const ZERO_TERMS = (terms) => terms.grossPaise === 0 && terms.discountsPaise === 0 && terms.refundsPaise === 0;

function ModeLine({ line }) {
  if (!line.length) return null;
  return (
    <p data-testid="cash-mode-line" className="cct-mode-line mt-2 text-xs text-slate-500 dark:text-slate-400">
      {CASH_TAB_COPY.paymentModeLine(line)}
    </p>
  );
}

/**
 * One technician-based diagnostic card. Lab and Radiology share the SAME wire
 * shape (`LabCashBlock`) and the SAME selectors (`selectLabCash`,
 * `selectCashSection(…, 'lab')`), so both render through this one component
 * (BUSY-VOUCHER R1 FE-BOARD, OR-54: both cards always shown when present, ₹0
 * included). `testPrefix` keeps Lab's existing `lab-*` test ids and gives
 * Radiology its own `radiology-*` ids. `filterNote` is `null` unless a doctor
 * filter is active (neither card is ever filtered by doctor).
 */
function LabLikeCard({
  block, testPrefix, heading, totalLabel, emptyLine, caption, filterNote, resetKey,
}) {
  const cash = useMemo(() => selectLabCash(block), [block]);
  // By technician (recon item 17) — SAME selector every OPD/OTC table already
  // reads; `rows` is `[]` when `by_member` is absent (older BE).
  const memberSection = useMemo(() => selectCashSection(block, 'lab'), [block]);
  const modeLine = useMemo(() => selectPaymentModeLine(block?.by_mode), [block]);

  const empty = cash.grossPaise === 0 && cash.discountsPaise === 0 && cash.refundsPaise === 0;
  const hasWaterfall = cash.discountsPaise !== 0 || cash.refundsPaise !== 0;
  // `by_member` ABSENT (older BE build) => `rows` is `[]` => no table, the
  // card renders exactly as before (§API CONTRACT FE binding notes).
  const byMemberPresent = Array.isArray(block?.by_member);
  const byTechRows = byMemberPresent ? selectVisibleRows(memberSection?.rows) : [];
  const showByTechTable = byMemberPresent && byTechRows.length > 0;

  return (
    <div data-testid={`${testPrefix}-card`} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-50">{heading}</h3>
      {filterNote && (
        <p data-testid={`${testPrefix}-filter-note`} className="mb-2 text-xs text-slate-500 dark:text-slate-400">{filterNote}</p>
      )}
      {empty ? (
        <p data-testid={`${testPrefix}-empty`} className="text-sm text-slate-600 dark:text-slate-300">{emptyLine}</p>
      ) : (
        <>
          <div data-testid={`${testPrefix}-lines`} className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
              <div className="text-xs text-slate-500 dark:text-slate-400">OPD patients</div>
              <div className="mt-1 whitespace-nowrap text-base font-semibold text-slate-900 dark:text-slate-50">{cash.opd?.net}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
              <div className="text-xs text-slate-500 dark:text-slate-400">Admitted patients (IPD)</div>
              <div className="mt-1 whitespace-nowrap text-base font-semibold text-slate-900 dark:text-slate-50">{cash.ipd?.net}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
              <div className="text-xs text-slate-500 dark:text-slate-400">{totalLabel}</div>
              <div className="mt-1 whitespace-nowrap text-base font-semibold text-slate-900 dark:text-slate-50">{cash.net}</div>
            </div>
          </div>
          {hasWaterfall && (
            <p data-testid={`${testPrefix}-waterfall`} className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Gross collected <span className="whitespace-nowrap">{cash.gross}</span> · Less: Discounts <span className="whitespace-nowrap">{cash.discounts}</span> · Less: Refunds <span className="whitespace-nowrap">{cash.refunds}</span>
            </p>
          )}
          <ModeLine line={modeLine} />
          {showByTechTable && (
            <div data-testid={`${testPrefix}-by-tech`} className="mt-3">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {CASH_TAB_COPY.labByTechHeading}
              </h4>
              <CashMemberTable
                rows={byTechRows}
                footerTerms={memberSection.terms}
                footerLabel={CASH_TAB_COPY.footerOrg}
                personHeader="Lab technician"
                showRole
                showKindColumns={false}
                isRowClickable={() => false}
                searchPlaceholder={CASH_TAB_COPY.labSearchPlaceholder}
                resetKey={resetKey}
              />
            </div>
          )}
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{caption}</p>
        </>
      )}
    </div>
  );
}

/**
 * CashCollectionsTab (DOCTOR-REPORTS P2, FE-3) — the "OPD, OTC, Lab &
 * Diagnostic" tab body: basis banner + stacked cards (build-plan.md FE-3, mockup
 * variant-a-stacked-tab.html). Single-fetch (D1) — this component never
 * fetches; every figure comes from the ONE board response the page already
 * holds. Lab hides gracefully when the `lab` key is absent (§API CONTRACT:
 * off unless the org's lab module is on or a lab row exists this month).
 * BUSY-VOUCHER R1: Radiology is a second card on `data.radiology` (present
 * whenever `lab` is, ₹0 included); an absent key hides just that card.
 *
 * Doctor filter (D5/S8): OPD and OTC collapse to the ONE selected member's
 * row (not a highlighted row among many — build-plan FE-3, supersedes the
 * mockup's "keep all rows" sketch); Lab and Radiology NEVER change with the
 * filter. The payment-mode line under each card always reads the section BLOCK's
 * `by_mode` (organisation-level), never the filtered member's own mode
 * split — the server doesn't compute one.
 */
export default function CashCollectionsTab({
  data, selectedKey = null, selectedLabel = null, filterKeys, onSelectKey,
}) {
  const opd = data?.opd;
  const otc = data?.otc;
  const lab = data?.lab;
  const radiology = data?.radiology;
  const periodLabel = data?.period?.label;
  const keys = filterKeys || new Set();

  const opdSection = useMemo(() => selectCashSection(opd, 'opd'), [opd]);
  const otcSection = useMemo(() => selectCashSection(otc, 'otc'), [otc]);
  const opdModeLine = useMemo(() => selectPaymentModeLine(opd?.by_mode), [opd]);
  const otcModeLine = useMemo(() => selectPaymentModeLine(otc?.by_mode), [otc]);
  const truncated = selectCashTruncated(data?.cash_coverage);

  if (!opdSection || !otcSection) return null;

  const opdFilteredRow = selectedKey ? selectCashRowForKey(opd, selectedKey) : null;
  const otcFilteredRow = selectedKey ? selectCashRowForKey(otc, selectedKey) : null;
  const opdFilteredRowShaped = opdFilteredRow ? opdSection.rows.find((r) => r.key === selectedKey) : null;
  const otcFilteredRowShaped = otcFilteredRow ? otcSection.rows.find((r) => r.key === selectedKey) : null;

  const opdOrgEmpty = ZERO_TERMS(opdSection.terms);
  const otcOrgEmpty = ZERO_TERMS(otcSection.terms);
  const opdShowEmpty = selectedKey ? !opdFilteredRowShaped : opdOrgEmpty;
  const otcShowEmpty = selectedKey ? !otcFilteredRowShaped : otcOrgEmpty;

  const anyNegative = opdSection.rows.some((r) => r.isNegative);

  return (
    <div data-testid="cash-collections-tab" className="flex flex-col gap-3">
      <div data-testid="cash-basis-banner" className="cct-basis-banner rounded-lg border p-3 text-sm text-slate-700 dark:text-slate-200">
        <p>{CASH_TAB_COPY.basisLine(periodLabel)}</p>
        <p className="mt-1 font-medium">{CASH_TAB_COPY.doNotAddLine}</p>
      </div>

      {truncated && (
        <div role="alert" data-testid="cash-truncated-banner" className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100">
          {CASH_TAB_COPY.truncatedBanner(periodLabel)}
        </div>
      )}

      {/* OPD card */}
      <div data-testid="opd-card" className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
        <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-50">{CASH_TAB_COPY.opdHeading}</h3>
        {opdShowEmpty ? (
          <p data-testid="opd-empty" className="text-sm text-slate-600 dark:text-slate-300">
            {selectedKey ? CASH_TAB_COPY.filteredOpdEmpty(selectedLabel, periodLabel) : CASH_TAB_COPY.emptyOpd(periodLabel)}
          </p>
        ) : (
          <>
            {selectedKey && (
              <p data-testid="opd-filter-notice" className="mb-2 text-xs text-teal-700 dark:text-teal-300">
                {CASH_TAB_COPY.opdFilterNotice(selectedLabel)}
              </p>
            )}
            <CashMemberTable
              rows={opdFilteredRowShaped ? [opdFilteredRowShaped] : selectVisibleRows(opdSection.rows)}
              footerTerms={opdFilteredRowShaped || opdSection.terms}
              footerLabel={opdFilteredRowShaped ? CASH_TAB_COPY.footerFor(selectedLabel) : CASH_TAB_COPY.footerOrg}
              personHeader="Doctor"
              showRole={false}
              selectedKey={selectedKey}
              isRowClickable={(row) => keys.has(row.key)}
              onRowSelect={onSelectKey}
              searchPlaceholder="Search doctor…"
              resetKey={data?.period?.month}
            />
            <ModeLine line={opdModeLine} />
            {anyNegative && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{CASH_TAB_COPY.negativeNote}</p>}
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{CASH_TAB_COPY.opdCaption}</p>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{CASH_TAB_COPY.discountFootnote}</p>
          </>
        )}
      </div>

      {/* OTC card */}
      <div data-testid="otc-card" className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
        <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-50">{CASH_TAB_COPY.otcHeading}</h3>
        {otcShowEmpty ? (
          <p data-testid="otc-empty" className="text-sm text-slate-600 dark:text-slate-300">
            {selectedKey ? CASH_TAB_COPY.filteredOtcEmpty(selectedLabel, periodLabel) : CASH_TAB_COPY.emptyOtc(periodLabel)}
          </p>
        ) : (
          <>
            <CashMemberTable
              rows={otcFilteredRowShaped ? [otcFilteredRowShaped] : selectVisibleRows(otcSection.rows)}
              footerTerms={otcFilteredRowShaped || otcSection.terms}
              footerLabel={otcFilteredRowShaped ? CASH_TAB_COPY.footerFor(selectedLabel) : CASH_TAB_COPY.footerOrg}
              personHeader="Staff / doctor"
              showRole
              selectedKey={selectedKey}
              isRowClickable={(row) => row.role === STAFF_ROLES.DOCTOR || (row.isUnassigned && keys.has(row.key))}
              onRowSelect={onSelectKey}
              searchPlaceholder="Search staff or doctor…"
              resetKey={data?.period?.month}
            />
            <ModeLine line={otcModeLine} />
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{CASH_TAB_COPY.otcCaption}</p>
          </>
        )}
      </div>

      {/* Lab card — hidden entirely when the `lab` key is absent. */}
      {lab && (
        <LabLikeCard
          block={lab}
          testPrefix="lab"
          heading={CASH_TAB_COPY.labHeading}
          totalLabel={CASH_TAB_COPY.labTotalLabel}
          emptyLine={CASH_TAB_COPY.emptyLab(periodLabel)}
          caption={radiology ? `${CASH_TAB_COPY.labCaption} ${CASH_TAB_COPY.labImagingNote}` : CASH_TAB_COPY.labCaption}
          filterNote={selectedKey ? CASH_TAB_COPY.labFilterNote : null}
          resetKey={data?.period?.month}
        />
      )}

      {/* Radiology card (BUSY-VOUCHER R1) — hidden when the `radiology` key is absent. */}
      {radiology && (
        <LabLikeCard
          block={radiology}
          testPrefix="radiology"
          heading={CASH_TAB_COPY.radiologyHeading}
          totalLabel={CASH_TAB_COPY.radiologyTotalLabel}
          emptyLine={CASH_TAB_COPY.emptyRadiology(periodLabel)}
          caption={CASH_TAB_COPY.radiologyCaption}
          filterNote={selectedKey ? CASH_TAB_COPY.radiologyFilterNote : null}
          resetKey={data?.period?.month}
        />
      )}
    </div>
  );
}
